import { AxiosResponse } from 'axios';
import { filterByIdOrShortId } from '@/lib/shortId';
import { supabase } from '@/lib/supabase';
import { asResponse, paginationRange, buildPaginatedResponse, throwIfError } from '@/lib/supabase-helpers';
import { ResponseWithPagination } from '@/types';
import { SeoContentAnalysis } from '@/types/api/seo';

function mapAnalysis(row: any): SeoContentAnalysis {
  return {
    id: row.id,
    shortId: row.short_id ?? row.id,
    projectId: row.project_id,
    url: row.url,
    targetKeyword: row.target_keyword,
    overallScore: row.overall_score,
    status: row.status,
    results: row.results,
    createdBy: row.created_by,
    createdAt: row.created_at,
  };
}

const seoContentService = {
  async getAll({
    projectId,
    page,
    take,
  }: {
    projectId: string;
    page: number;
    take: number;
  }): Promise<AxiosResponse<ResponseWithPagination<SeoContentAnalysis[]>>> {
    const [from, to] = paginationRange(page, take);
    const { data, error, count } = await supabase
      .from('seo_content_analyses')
      .select('*', { count: 'exact' })
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) throw error;
    return asResponse(
      buildPaginatedResponse((data ?? []).map(mapAnalysis), count ?? 0, page, take)
    );
  },

  async getById(id: string): Promise<AxiosResponse<SeoContentAnalysis>> {
    let query = supabase
      .from('seo_content_analyses')
      .select('*');
    query = filterByIdOrShortId(query, id);
    const { data, error } = await query.single();

    if (error) throw error;
    return asResponse(mapAnalysis(data));
  },

  async create(payload: {
    projectId: string;
    url: string;
    targetKeyword: string;
    createdBy: string;
  }): Promise<AxiosResponse<SeoContentAnalysis>> {
    const result = await supabase
      .from('seo_content_analyses')
      .insert({
        project_id: payload.projectId,
        url: payload.url,
        target_keyword: payload.targetKeyword,
        status: 'queued',
        created_by: payload.createdBy,
      })
      .select()
      .single();

    return asResponse(mapAnalysis(throwIfError(result)));
  },

  async delete(id: string): Promise<AxiosResponse<void>> {
    const { error } = await supabase
      .from('seo_content_analyses')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return asResponse(undefined as unknown as void);
  },
};

export default seoContentService;
